<template>
  <div class="inspection-category-builder">
    <draggable
      v-model="sections"
      data-me="setting-vehicle-inspection-category-draggable"
      class="inspection-category-builder__list"
      handle=".inspection-category-card__drag-handle"
      :animation="150"
      @start="handleDragStart"
      @end="handleDragEnd"
    >
      <InspectionCategoryCard
        v-for="(section, sectionIndex) in sections"
        :key="section.key"
        :section="section"
        :viewOnly="viewOnly"
        :isDragging="section.key === draggedSectionKey"
        :canRemove="sections.length > 1"
        :sections="sections"
        :sectionIndex="sectionIndex"
        :shouldFocusName="section.key === focusCategoryKey"
        :focusQuestionKey="focusQuestionKey"
        @expand-section="handleCategoryExpand(sectionIndex)"
        @update-section="handleCategoryUpdate(sectionIndex, $event)"
        @remove="handleCategoryRemove(sectionIndex)"
        @duplicate="handleCategoryDuplicate(sectionIndex)"
        @add-question="handleQuestionAdd(sectionIndex)"
        @remove-question="handleQuestionRemove(sectionIndex, $event)"
        @duplicate-question="handleQuestionDuplicate(sectionIndex, $event)"
      />
    </draggable>
    <CategoryAddSection v-if="!viewOnly" @add="handleCategoryAdd" />
  </div>
</template>

<script>
import draggable from 'vuedraggable'
import CategoryAddSection from './CategoryAddSection.vue'
import InspectionCategoryCard from './InspectionCategoryCard.vue'
import { DEFAULT_ANSWER_OPTIONS, QUESTION_TYPES } from '../../lib/constants'
import { generateUniqueId, getDuplicateName } from '../../lib/utils'

export default {
  components: {
    draggable,
    CategoryAddSection,
    InspectionCategoryCard,
  },
  props: {
    value: {
      type: Array,
      default: () => [],
    },
    viewOnly: {
      type: Boolean,
      default: false,
    },
  },
  emits: ['input'],
  data() {
    return {
      draggedSectionKey: null,
      focusCategoryKey: null,
      focusQuestionKey: null,
    }
  },
  computed: {
    sections: {
      get() {
        return this.value
      },
      set(nextSections) {
        this.emitNextSections(nextSections)
      },
    },
  },
  mounted() {
    if (!this.viewOnly && !this.sections.length) {
      this.emitNextSections([this.createEmptyCategory()])
    }
  },
  methods: {
    handleDragStart({ oldIndex }) {
      this.draggedSectionKey = this.sections[oldIndex].key
    },
    handleDragEnd(event) {
      this.draggedSectionKey = null
      if (event.newIndex !== event.oldIndex) this.handleCategoryExpand(event.newIndex)
    },
    createEmptyQuestion(category = { questions: [] }) {
      return {
        key: generateUniqueId('question'),
        name: `${this.$t('vehicleInspection.fields.question')} ${category.questions.length + 1}`,
        type: QUESTION_TYPES.options,
        options: DEFAULT_ANSWER_OPTIONS.map((option) => ({
          ...option,
          label: this.$t(option.label),
        })),
        isRequired: true,
        isNoteEnabled: false,
        isPhotoEnabled: false,
        notePolicy: null,
        photoPolicy: null,
      }
    },
    createEmptyCategory() {
      return {
        key: generateUniqueId('section'),
        name: `${this.$t('global.field.category')} ${this.sections.length + 1}`,
        questions: [this.createEmptyQuestion()],
        isExpanded: true,
      }
    },
    emitNextSections(nextSections) {
      this.$emit('input', nextSections)
    },
    handleCategoryAdd() {
      this.emitNextSections([
        ...this.sections.map((category) => ({
          ...category,
          isExpanded: false,
        })),
        this.createEmptyCategory(),
      ])
    },
    handleCategoryExpand(categoryIndex) {
      const nextSections = this.sections.map((category, index) => ({
        ...category,
        isExpanded: index === categoryIndex ? !category.isExpanded : false,
      }))
      this.emitNextSections(nextSections)
    },
    handleCategoryUpdate(categoryIndex, nextCategory) {
      const nextSections = [...this.sections]
      nextSections.splice(categoryIndex, 1, nextCategory)
      this.emitNextSections(nextSections)
    },
    handleCategoryRemove(categoryIndex) {
      const nextSections = [...this.sections]
      nextSections.splice(categoryIndex, 1)
      this.emitNextSections(nextSections)
    },
    handleCategoryDuplicate(categoryIndex) {
      const category = this.sections[categoryIndex]
      if (!category) return

      const duplicateCategory = {
        ...category,
        key: generateUniqueId('section'),
        sectionId: null,
        name: getDuplicateName(
          category.name,
          this.sections.map((section) => section.name)
        ),
        isExpanded: true,
        questions: category.questions.map((question) => ({
          ...question,
          key: generateUniqueId('question'),
          questionId: null,
        })),
      }

      const nextSections = this.sections.map((section) => ({ ...section, isExpanded: false }))
      nextSections.splice(categoryIndex + 1, 0, duplicateCategory)
      this.focusCategoryKey = duplicateCategory.key
      this.emitNextSections(nextSections)
    },
    handleQuestionAdd(categoryIndex) {
      const category = this.sections[categoryIndex]
      const nextCategory = {
        ...category,
        questions: [...category.questions, this.createEmptyQuestion(category)],
      }

      this.handleCategoryUpdate(categoryIndex, nextCategory)
    },
    handleQuestionRemove(categoryIndex, questionIndex) {
      const category = this.sections[categoryIndex]
      const nextQuestions = [...category.questions]
      nextQuestions.splice(questionIndex, 1)

      const nextCategory = {
        ...category,
        questions: nextQuestions,
      }

      this.handleCategoryUpdate(categoryIndex, nextCategory)
    },
    handleQuestionDuplicate(categoryIndex, questionIndex) {
      const category = this.sections[categoryIndex]
      const question = category.questions[questionIndex]

      if (!question) return

      const duplicateQuestion = {
        ...question,
        key: generateUniqueId('question'),
        questionId: null,
        name: getDuplicateName(
          question.name,
          category.questions.map((question) => question.name)
        ),
      }

      const nextQuestions = [...category.questions]
      nextQuestions.splice(questionIndex + 1, 0, duplicateQuestion)

      const nextCategory = {
        ...category,
        questions: nextQuestions,
      }

      this.focusQuestionKey = duplicateQuestion.key
      this.handleCategoryUpdate(categoryIndex, nextCategory)
    },
  },
}
</script>

<style lang="scss" scoped>
.inspection-category-builder {
  &__list {
    display: grid;
    gap: 14px;
  }
}
</style>
